import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Grid from '@material-ui/core/Grid/Grid';

import SubmissionCard from 'Layouts/SubmissionManagement/SubmissionCard';
import Tabs from 'Components/Tabs/Tabs';
import VersionHistoryList from './components/VersionHistoryList';
import Activities from './components/Activities';
import Emails from './components/Emails';
import SubmissionTimeline from './containers/SubmissionTimeline';
import { pageTitleDecorator, authenticatedPageDecorator } from 'Decorators';
import { changeStatus, loadPhases, loadSubmissionData } from 'Actions/submission';
import {
    PROCESS_STATUSES,
    PROCESS_STATUS_CLASSES,
    ROLE_ADMIN,
    ROLE_CHIEF_EDITORIAL,
    ROLE_EDITORIAL,
    ROLE_EDITOR, ROLE_EDITORIAL_BOARD, ROLE_REVIEWER, ROLE_AUTHOR, PROCESS_STATUS_IN_PROGRESS,
} from 'Constants';
import OVERDUE from 'Layouts/Layout/images/ic-overdue.svg';
import Loader from 'Common/Loader/Loader';
import Lightbox from 'Common/Lightbox';
import DialogActions from '@material-ui/core/DialogActions/DialogActions';
import Button from 'Common/CustomButtons/Button';

@connect(
    store => ({
        loading: store.submissionData.loading,
        submissionData: store.submissionData.submission,
        phasesList: store.submissionPhases.data,
        user: store.auth.user,
    }),
    dispatch => ({
        loadSubmissionData: id => dispatch(loadSubmissionData(id)),
        loadPhases: () => dispatch(loadPhases()),
        changeStatus: (id, data) => dispatch(changeStatus(id, data))
    })
)
@authenticatedPageDecorator()
@pageTitleDecorator('submissions')
export default class SubmissionManagement extends PureComponent {
    /**
     * @type {Object}
     */
    static propTypes = {
        match: PropTypes.object,
        profileData: PropTypes.object,
        loading: PropTypes.bool,
        history: PropTypes.object.isRequired,
        submissionData: PropTypes.object.isRequired,
        phasesList: PropTypes.array.isRequired,
        loadSubmissionData: PropTypes.func.isRequired,
        loadPhases: PropTypes.func.isRequired,
        isOneOf: PropTypes.func.isRequired,
        is: PropTypes.func.isRequired,
        changeStatus: PropTypes.func.isRequired,
        location: PropTypes.any,
        user: PropTypes.object
    };

    /**
     * @type {Object}
     */
    static contextTypes = {
        intl: PropTypes.object.isRequired,
    };

    state = {
        isOpenAuthorReminder: false
    };

    componentDidMount() {
        const { loadSubmissionData, loadPhases, match } = this.props;
        loadPhases();
        loadSubmissionData(match.params.id);
    }

    isAdminUser() {
        const { isOneOf } = this.props;
        return isOneOf([ROLE_ADMIN, ROLE_CHIEF_EDITORIAL, ROLE_EDITORIAL, ROLE_EDITOR]);
    }

    isPhase = phase => {
        const {
            submissionData: {
                status: { alias },
            },
        } = this.props;
        return alias === phase;
    };

    isOneOfPhases = phases => {
        const {
            submissionData: {
                status: { alias },
            },
        } = this.props;
        return phases.includes(alias);
    };

    isStatus = status => {
        const {
            submissionData: { process_status },
        } = this.props;
        return process_status === status;
    };

    tabs = [
        {
            name: 'general',
            text: 'submission.tab.general',
        },
        {
            name: 'emails',
            text: 'submission.tab.emails',
            hidden: !this.isAdminUser(),
        },
        {
            name: 'files',
            text: 'submission.tab.files',
            hidden: !this.isAdminUser(),
        },
        {
            name: 'activities',
            text: 'submission.tab.activities',
            hidden: !this.isAdminUser(),
        },
    ];

    handleClick = tab => {
        const {
            history,
            match: { params },
        } = this.props;

        history.push(`/submissions/${params.id}/${tab}`);
    };

    getActiveTab = () => {
        const { match } = this.props;
        return match.params && match.params.tab && this.isAdminUser() ? match.params.tab : 'general';
    };

    /**
     * Render submission tab
     *
     * @returns {*}
     */
    renderTab = () => {
        const {
            submissionData,
            phasesList,
            match: {
                params: { id },
            },
        } = this.props;

        if (!submissionData) return null;

        switch (this.getActiveTab()) {
            case 'emails':
                return <Emails submissionId={id} submissionData={submissionData} />;
            case 'files':
                return <VersionHistoryList submissionId={id} />;
            case 'activities':
                return <Activities submissionId={id} />;
            default:
                return (
                    <SubmissionCard
                        isPhase={this.isPhase}
                        isOneOfPhases={this.isOneOfPhases}
                        isStatus={this.isStatus}
                        submissionData={submissionData}
                        submissionId={id}
                        phasesList={phasesList}
                        onFileUpload={this.onFileUpload}
                    />
                );
        }
    };

    /**
     * Render submission timeline
     *
     * @returns {*}
     */
    renderTimeline = () => {
        const { submissionData : {status_id, results, process_status, created_at, updated_at}, phasesList } = this.props;
        const timelinePhases = [{id: 0, time_limit: 0, timeline_title: this.context.intl.formatMessage({ id: 'submission.phase.new' })}, ...phasesList];
        return status_id &&
            <SubmissionTimeline
                activePhase={status_id}
                activeStatus={process_status}
                completedPhases={results}
                phasesList={timelinePhases}
                created={created_at}
                updated={updated_at}
            />;
    };

    /**
     * Render submission title
     *
     * @returns {*}
     */
    renderTitle = () => {
        const { submissionData } = this.props;

        return (
            <div className="data-table-filters-wrap-block" style={{ paddingTop: '10px' }}>
                <div className="data-table-title-block">
                    <FormattedMessage id={'title.submission.edit'} /> <span>#MA{submissionData.id}</span>
                </div>
                <div
                    className={`submission-status-title submission-status-title-${
                        PROCESS_STATUS_CLASSES[submissionData.process_status]
                    }`}
                >
                    <span> {PROCESS_STATUSES[submissionData.process_status]} </span>
                    {!!submissionData.is_overdue && <img src={OVERDUE} />}
                </div>
            </div>
        );
    };

    redirect = (from, pathname) => {
        const { history } = this.props;
        history.replace({
            pathname,
            state: { from }
        });
    };

    onFileUpload = () => {
        const {is} = this.props;

        if (is(ROLE_AUTHOR)) this.setState({isOpenAuthorReminder: true});
    }

    handleCloseLightBox = () => {
        this.setState({isOpenAuthorReminder: false});
    }

    handleChangeStatus = () => {
        const {changeStatus, submissionData: {id}} = this.props;

        changeStatus(id, {status: PROCESS_STATUS_IN_PROGRESS, email: {}, isReviewer: 0});
        this.setState({isOpenAuthorReminder: false});
    }

    /**
     * Render
     *
     * @returns {*}
     */
    render() {
        const {isOpenAuthorReminder} = this.state;
        const { isOneOf, is, loading, submissionData, match, user, location: { pathname } } = this.props;
        if (!submissionData || submissionData.id !== parseInt(match.params.id)) {
            return <Loader/>;
        }

        if (submissionData && submissionData.id && (
            (is(ROLE_AUTHOR) && submissionData.user_id !== user.id) ||
            (is(ROLE_EDITOR) && submissionData.user_id === user.id)
        )) {
            this.redirect(pathname, '/403');
        }

        return (
            <div>
                {loading && <Loader/>}

                {this.renderTitle()}

                {!isOneOf([ROLE_EDITORIAL_BOARD, ROLE_REVIEWER]) &&
                    this.renderTimeline()}

                <Grid container spacing={24}>
                    <Grid item xs={isOneOf([ROLE_EDITORIAL_BOARD, ROLE_REVIEWER, ROLE_AUTHOR]) ? 12 : 10}>
                        <div>{this.renderTab()}</div>
                    </Grid>
                    {!isOneOf([ROLE_EDITORIAL_BOARD, ROLE_REVIEWER, ROLE_AUTHOR]) && <Grid item xs={2}>
                        <Tabs
                            tabs={this.tabs}
                            active={this.getActiveTab()}
                            handleClick={this.handleClick}
                            values={{
                                newEmailsCount: submissionData.emailsNew && submissionData.emailsNew.length ?
                                    (<span className='new_emails_counter'>&nbsp;({submissionData.emailsNew.length})</span>) :
                                    ''
                            }}
                        />
                    </Grid>}
                </Grid>

                <Lightbox
                    maxWidth="md"
                    handleClose={this.handleCloseLightBox}
                    isOpen={isOpenAuthorReminder}
                    title="title.author.reminder_after_upload.title"
                    text={
                        <Fragment>
                            <div>{'Thank you for the revised file uploading!'}</div>
                            <div>{'If this file is final - please click the "Change status" button at the bottom of this dialog.'}</div>
                            <div>{'If you want to double-check your file - please click the "Skip" button, but don\'t forget to submit your changes later on the submission page!'}</div>
                        </Fragment>
                    }
                    dialogActions={false}
                >
                    <DialogActions className='emails-dialog-wrapper'>
                        <Button type="button" onClick={this.handleCloseLightBox} customClass='lightbox-submit-skip-btn upload-style-btn'>
                            <FormattedMessage id={'button.lightbox.skip'} />
                        </Button>

                        <Button type="button" onClick={this.handleChangeStatus} color="success" right customClass='lightbox-submit-btn'>
                            <FormattedMessage id={'button.lightbox.status_change'} />
                        </Button>


                    </DialogActions>
                </Lightbox>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/layouts/SubmissionManagement/SubmissionManagement.jsx